import Router from 'next/router';
import { isEmpty } from 'ramda';
import { ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  startWith,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { Countries, Users, Weddings } from '@bridebook/models';
import { getScreenTypeByWidth } from '@bridebook/ui/src/utils/get-screen-type-by-width';
import { getWindowSizes } from '@bridebook/web/app-shared/lib/app/utils/get-window-sizes';
import { appError } from '@bridebook/web/lib/app/actions';
import { TaskActionTypes } from '@bridebook/web/lib/task/action-types';
import {
  taskFlowDefinitionInitialized,
  userTasksLoaded,
  weddingTasksLoaded,
} from '@bridebook/web/lib/task/actions';
import { IApplicationState, IEpicDeps } from '@bridebook/web/lib/types';
import { noopAction } from '@bridebook/web/lib/utils';
import {
  buildMobileFlowDefinition,
  MOBILE_FLOW_IDS,
} from '@bridebook/web/components/task/today-task/mobile/flow-definition';
import { resolveMobileAssignment } from '@bridebook/web/components/task/today-task/mobile/assignment';
import { TodayTaskFlowDefinitionApi } from '@bridebook/toolbox/src/today-task/types';
import { recursiveSerializeTimestamps } from '@bridebook/toolbox/src/serialize-timestamps';

const mapFlowCollectionResponse = (response: {
  fromCache: boolean;
  result: Record<string, TodayTaskFlowDefinitionApi>;
}) => {
  if (response.fromCache && isEmpty(response.result)) {
    return noopAction();
  }
  const flows = Object.values(response.result);
  if (flows.length === 0 || !flows[0]) {
    return noopAction();
  }
  return taskFlowDefinitionInitialized({ definition: flows[0] });
};

export const initWeddingTodayTaskListener = (action$: Observable<any>, { state$ }: IEpicDeps) =>
  action$.pipe(
    ofType(TaskActionTypes.INIT_TODAY_TASK_LISTENER),
    withLatestFrom(state$),
    filter(([_, state]: [any, IApplicationState]) => Boolean(state.weddings.profile.id)),
    switchMap(([_, state]: [any, IApplicationState]) => {
      const weddingId = state.weddings.profile.id;
      return Weddings._.getById(weddingId)
        .Tasks.queryTodayTasks()
        .observe(true, false)
        .pipe(
          mergeMap((tasks) => of(weddingTasksLoaded(recursiveSerializeTimestamps(tasks)))),
          takeUntil(action$.pipe(ofType(TaskActionTypes.STOP_TODAY_TASK_LISTENER))),
          catchError((error: Error) =>
            of(appError({ error, feature: 'Init wedding today task listener error' })),
          ),
        );
    }),
  );

export const initUserTodayTaskListener = (action$: Observable<any>, { state$ }: IEpicDeps) =>
  action$.pipe(
    ofType(TaskActionTypes.INIT_TODAY_TASK_LISTENER),
    withLatestFrom(state$),
    filter(([_, state]: [any, IApplicationState]) => Boolean(state.users.user?.id)),
    switchMap(([_, state]: [any, IApplicationState]) => {
      const userId = state.users.user?.id;
      if (!userId) throw new Error('Something went wrong');
      return Users._.getById(userId)
        .Tasks.queryTodayTasks()
        .observe(true, false)
        .pipe(
          mergeMap((tasks) => of(userTasksLoaded(recursiveSerializeTimestamps(tasks)))),
          takeUntil(action$.pipe(ofType(TaskActionTypes.STOP_TODAY_TASK_LISTENER))),
          catchError((error: Error) =>
            of(appError({ error, feature: 'Init user today task listener error' })),
          ),
        );
    }),
  );

export const initActiveTodayTaskDefinition = (action$: Observable<any>, { state$ }: IEpicDeps) =>
  action$.pipe(
    ofType(TaskActionTypes.INIT_TODAY_TASK_LISTENER),
    switchMap(() => {
      const todayTaskFlowId = Router?.query['todayTaskFlowId'] as string | undefined;

      if (todayTaskFlowId) {
        // eslint-disable-next-line no-console
        console.log('Selected task flow active: ' + todayTaskFlowId);
        return Countries._.getById('*')
          .TodayTaskFlows.getById(todayTaskFlowId)
          .observe(true, true)
          .pipe(
            map((response) => {
              // Check if the response is from cache and if the result is empty. If both conditions are true, exit the function to prevent issues when the client is offline and the cache data is empty.
              if (response.fromCache && isEmpty(response.result)) {
                return noopAction();
              }

              const taskFlowDefinitions = response.result;

              return taskFlowDefinitionInitialized({
                definition: taskFlowDefinitions,
              });
            }),
            takeUntil(action$.pipe(ofType(TaskActionTypes.STOP_TODAY_TASK_LISTENER))),
          );
      } else {
        const createTodayTasksStream = (isMobile: boolean) => {
          const todayTaskFlowsRoot = Countries._.getById('*').TodayTaskFlows;
          // Mobile users run the mobile flow; desktop uses the active flow.
          if (isMobile) {
            return state$.pipe(
              map((state: IApplicationState) => {
                const weddingsLoaded = state.weddings.loaded;
                const shortlistReady = state.shortlist.loaded;
                const wedding = state.weddings.profile;
                const user = state.users.user;
                return {
                  ready: weddingsLoaded && shortlistReady && !!user,
                  wedding,
                  user,
                  state,
                };
              }),
              filter((st) => st.ready),
              map(({ state, ...st }) => ({
                ...st,
                assignment: resolveMobileAssignment(state),
              })),
              distinctUntilChanged(
                (prev, curr) =>
                  prev.assignment === curr.assignment &&
                  prev.wedding?.id === curr.wedding?.id &&
                  prev.wedding?.l10n?.country === curr.wedding?.l10n?.country &&
                  prev.user?.id === curr.user?.id,
              ),
              switchMap(({ assignment }) => {
                const flowId = MOBILE_FLOW_IDS[assignment];
                const fallbackDefinition = buildMobileFlowDefinition(assignment);
                return todayTaskFlowsRoot
                  .getById(flowId)
                  .observe(true, true)
                  .pipe(
                    map((response) => {
                      const definition = response.result;
                      if (!definition || isEmpty(definition)) {
                        return taskFlowDefinitionInitialized({ definition: fallbackDefinition });
                      }

                      return taskFlowDefinitionInitialized({ definition });
                    }),
                    startWith(taskFlowDefinitionInitialized({ definition: fallbackDefinition })),
                  );
              }),
            );
          } else {
            return todayTaskFlowsRoot.active
              .observe(true, true)
              .pipe(map(mapFlowCollectionResponse));
          }
        };

        const { width } = getWindowSizes();
        const { isMobile } = getScreenTypeByWidth({
          width,
          isMobileUA: false,
          isTabletUA: false,
        });

        const tasks$ = createTodayTasksStream(isMobile);

        return tasks$.pipe(
          takeUntil(action$.pipe(ofType(TaskActionTypes.STOP_TODAY_TASK_LISTENER))),
          catchError((error: Error) =>
            of(appError({ error, feature: 'Init today task listener error' })),
          ),
        );
      }
    }),
    catchError((error: Error) =>
      of(appError({ error, feature: 'Init wedding today task listener error' })),
    ),
  );
